import { vars } from "@/shared/lib";
import { COMPANY_STATUS, COMPANY_STATUS_KEY_TYPE, CORPORATION, CORPORATION_KEY_TYPE } from "@/shared/model";
import { ReactNode } from "@tanstack/react-router";
import { HTMLAttributes, RefObject, useEffect, useRef, useState } from "react";

import { create } from "zustand";

type NaverMapState = {
	naverMap?: naver.maps.Map;
	naverMarkers: Set<naver.maps.Marker>;
};

type NaverMapAction = {
	setNaverMap: (newNaver: naver.maps.Map) => void;
	removeNaverMap: () => void;

	addMarker: (newMarker: naver.maps.Marker) => void;
	resetMarkers: () => void;
};

export const useNaverMap = create<NaverMapState & NaverMapAction>((_set, _get) => ({
	naverMap: undefined,
	setNaverMap: (newNaver) => {
		_set({ naverMap: newNaver });
	},
	removeNaverMap: () => {
		_set({ naverMap: new naver.maps.Map(MAP_ID) });
	},

	naverMarkers: new Set(),
	addMarker: (newMarker) => {
		_set({ naverMarkers: new Set(_get().naverMarkers).add(newMarker) });
	},
	resetMarkers: () => {
		for (const marker of _get().naverMarkers) {
			marker.setMap(null);
		}

		_set({ naverMarkers: new Set() });
	}
}));

const MAP_ID = "naver_map";
const NCP_CLIENT_ID = "w9girpobu8";
const MAP_SRC = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${NCP_CLIENT_ID}`;

const MAP_CLUSTERING_ID = "naver_map_clustering";
const MAP_CLUSTERING_SRC = window.origin + "/MarkerClustering.js";

type NaverMapProps = {
	mapRef: RefObject<naver.maps.Map | null>;
	children?: ReactNode & ReactNode[];
	onChangeBounds: (bounds: naver.maps.LatLngBounds) => void;
} & HTMLAttributes<HTMLDivElement>;

function NaverMap({ mapRef, style, children, onChangeBounds }: NaverMapProps) {
	const [isLoaded, setIsLoaded] = useState<boolean>(false);
	const setNaverMap = useNaverMap((state) => state.setNaverMap);
	// const mapRef = useRef<naver.maps.Map>(null);
	const dragEndListener = useRef<naver.maps.MapEventListener>(null);
	const zoomListener = useRef<naver.maps.MapEventListener>(null);

	useEffect(() => {
		function initMap() {
			mapRef.current = new naver.maps.Map(MAP_ID, {
				center: new naver.maps.LatLng(37.49742165, 127.02999675000001),
				zoom: 10,
				maxZoom: 22,
				minZoom: 8,
				baseTileOpacity: 1,
				// =================
				// disableDoubleClickZoom: true,
				// disableDoubleTapZoom: true,
				// disableKineticPan: true,
				// disableTwoFingerTapZoom: true,
				// draggable: false,
				// keyboardShortcuts: false,
				// =================
				// pinchZoom: false,
				// scrollWheel: false,
				tileSpare: 10,
				tileTransition: true,
				tileDuration: 300,
				// =================
				mapTypeControl: false,
				mapTypeControlOptions: {
					mapTypeIds: [naver.maps.MapTypeId.NORMAL],
					position: naver.maps.Position.TOP_RIGHT,
					style: naver.maps.MapTypeControlStyle.BUTTON
				},
				zoomControl: true,
				zoomControlOptions: {
					style: naver.maps.ZoomControlStyle.LARGE,
					position: naver.maps.Position.RIGHT_CENTER
				},
				scaleControl: true,
				scaleControlOptions: {
					position: naver.maps.Position.BOTTOM_RIGHT
				},
				logoControl: false,
				// logoControlOptions: {
				// 	position: naver.maps.Position.TOP_LEFT
				// },
				mapDataControl: false,
				mapDataControlOptions: {
					position: naver.maps.Position.BOTTOM_LEFT
				}
			});

			// 지도 이동 및 확대/축소 이벤트에 대한 핸들러 등록
			dragEndListener.current = naver.maps.Event.addListener(mapRef.current, "dragend", updateBounds);
			zoomListener.current = naver.maps.Event.addListener(mapRef.current, "zoom_changed", updateBounds);

			setNaverMap(mapRef.current);
			setIsLoaded(true);
		}

		const clusteringScript = document.createElement("script");
		clusteringScript.id = MAP_CLUSTERING_ID;
		clusteringScript.src = MAP_CLUSTERING_SRC;
		clusteringScript.onload = initMap;

		const script = document.createElement("script");
		script.src = MAP_SRC;

		document.body.appendChild(script);
		document.body.appendChild(clusteringScript);

		return () => {
			dragEndListener.current && naver.maps.Event.removeListener(dragEndListener.current);
			zoomListener.current && naver.maps.Event.removeListener(zoomListener.current);

			document.body.removeChild(script);

			const clusteringScriptElement = document.body.querySelector("#" + MAP_CLUSTERING_ID);
			clusteringScriptElement && document.body.removeChild(clusteringScript);
		};
	}, []);

	const updateBounds = () => {
		if (mapRef.current) {
			const bounds = mapRef.current.getBounds() as naver.maps.LatLngBounds;
			// 추가적인 로직을 여기에 작성할 수 있습니다.
			onChangeBounds(bounds);
		}
	};

	return (
		<div id={MAP_ID} style={style}>
			{isLoaded && children ? children : null}
		</div>
	);
}

type MarkerProps = {
	lat: number;
	lng: number;
	displayName: string;
	companyStatus: COMPANY_STATUS_KEY_TYPE;
	corporationType: CORPORATION_KEY_TYPE;
};

const MARKER_WIDTH = 40;
const MARKER_HEIGHT = 20;

const FONT_COLOR: Record<COMPANY_STATUS_KEY_TYPE, string> = {
	UNKNOWN: vars.color.white,
	WITHDRAWN: vars.color.black,
	REGISTERED: vars.color.black,
	RE_REGISTERED: vars.color.white,
	MANAGEMENT: vars.color.white
};

export const MAP_BG_COLOR: Record<COMPANY_STATUS_KEY_TYPE, string> = {
	UNKNOWN: vars.color.gray[400], // 알수없음
	WITHDRAWN: vars.color.yellow, // 말소
	REGISTERED: vars.color.red, // 등록
	RE_REGISTERED: vars.color.gray[400], // 재등록
	MANAGEMENT: vars.color.green // 관리
};

export const createMarker = ({ lat, lng, displayName = "알수없음", companyStatus, corporationType }: MarkerProps) => {
	const markerContent = [
		`<section style="display: flex; 
		flex-direction: column; 
		padding: 12px 16px; 
		border: 1.2px solid ${vars.color.black}; 
		border-radius: ${corporationType === "PERSONAL" ? "999px" : "4px"}; 
		white-space: pre; 
		background-color: ${MAP_BG_COLOR[companyStatus]}">`,
		`<p style="font-size: 14px; font-weight: normal; color: ${FONT_COLOR[companyStatus]}">${displayName}</p>`,
		"</section>"
	].join("");

	const marker = new naver.maps.Marker({
		position: new naver.maps.LatLng(lat, lng),
		title: displayName,
		clickable: true,
		cursor: "pointer",
		icon: {
			content: markerContent,
			size: new naver.maps.Size(MARKER_WIDTH, MARKER_HEIGHT)
			// anchor: new naver.maps.Point(11, 35)
		}
	});

	return { marker };
};

type InfoWindowProps = {
	marker: naver.maps.Marker;
	id: string;
	name: string;
	corporationName: string;
	address: string;
	telephone: string;
	corporationType: CORPORATION_KEY_TYPE;
	companyStatus: COMPANY_STATUS_KEY_TYPE;
	managerName: string | null;
	registeredDate: string | null;
};

export const createInfoWindow = ({
	marker,
	id,
	name,
	corporationName,
	address,
	telephone,
	corporationType,
	companyStatus,
	managerName,
	registeredDate
}: InfoWindowProps) => {
	const naverMap = useNaverMap((state) => state.naverMap);

	const infoWindowContent = [
		`<section style="
			display: flex; 
			flex-direction: column; 
			gap: 4px; 
			padding: 8px 12px; 
			border: 0.5px solid black; 
			border-radius: 4px
			" 
			data-id="${id}"
		>`,
		`   <p style="font-size: 14px;">세무사명: ${name}</p>`,
		`   <p style="font-size: 14px;">사무실명: ${corporationName}</p>`,
		`   <p style="font-size: 14px;">주소: ${address}</p>`,
		`   <p style="font-size: 14px;">연락처: ${telephone}</p>`,
		`   <p style="font-size: 14px;">개인법인여부: ${CORPORATION[corporationType]}</p>`,
		`   <p style="font-size: 14px;">관리상태: ${COMPANY_STATUS[companyStatus]}</p>`,
		`   <p style="font-size: 14px;">담당자: ${managerName || "-"}</p>`,
		`   <p style="font-size: 14px;">등록일자: ${registeredDate ? registeredDate : "-"}</p>`,
		// `   <p style="font-size: 14px;">성별: ${corporationName}</p>`,
		"</section>"
	].join("");

	const infoWindow = new naver.maps.InfoWindow({
		content: infoWindowContent,
		borderWidth: 0,
		// anchorColor: vars.color.white,
		// anchorSize: {width: 20, height: 10},
		disableAnchor: true,
		disableAutoPan: false,
		pixelOffset: new naver.maps.Point(30, -5),
		zIndex: 10
	});

	if (!naverMap) return;

	// biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
	useEffect(() => {
		const listener = naver.maps.Event.addListener(marker, "click", function () {
			// naverMap.setCenter(marker.getPosition());
			infoWindow.getMap() ? infoWindow.close() : infoWindow.open(naverMap, marker);
		});

		return () => {
			infoWindow.getMap() && infoWindow.close();
			naver.maps.Event.removeListener(listener);
		};
	}, [marker, infoWindow, naverMap]);
};

function Marker({ marker }: { marker: naver.maps.Marker }) {
	const naverMap = useNaverMap((state) => state.naverMap);

	useEffect(() => {
		if (!naverMap) return;

		marker.setMap(naverMap);
		return () => {
			marker.setMap(null);
		};
	});

	return <></>;
}

const HOME_PATH = "https://navermaps.github.io/maps.js.ncp/docs";

function Clustering({ markers }: { markers: naver.maps.Marker[] }) {
	const naverMap = useNaverMap((state) => state.naverMap);

	const htmlMarker1 = {
		content:
			"<div style=\"cursor:pointer;width:50px;height:50px;line-height:52px;font-size:14px;color:white;text-align:center;font-weight:bold;background:url(" +
			HOME_PATH +
			"/img/cluster-marker-1.png);background-size:contain;\"></div>",
		size: new naver.maps.Size(50, 50),
		anchor: new naver.maps.Point(20, 20)
	},
		htmlMarker2 = {
			content:
				"<div style=\"cursor:pointer;width:50px;height:50px;line-height:52px;font-size:14px;color:white;text-align:center;font-weight:bold;background:url(" +
				HOME_PATH +
				"/img/cluster-marker-2.png);background-size:contain;\"></div>",
			size: new naver.maps.Size(50, 50),
			anchor: new naver.maps.Point(20, 20)
		},
		htmlMarker3 = {
			content:
				"<div style=\"cursor:pointer;width:50px;height:50px;line-height:52px;font-size:14px;color:white;text-align:center;font-weight:bold;background:url(" +
				HOME_PATH +
				"/img/cluster-marker-3.png);background-size:contain;\"></div>",
			size: new naver.maps.Size(50, 50),
			anchor: new naver.maps.Point(20, 20)
		},
		htmlMarker4 = {
			content:
				"<div style=\"cursor:pointer;width:50px;height:50px;line-height:52px;font-size:14px;color:white;text-align:center;font-weight:bold;background:url(" +
				HOME_PATH +
				"/img/cluster-marker-4.png);background-size:contain;\"></div>",
			size: new naver.maps.Size(50, 50),
			anchor: new naver.maps.Point(20, 20)
		},
		htmlMarker5 = {
			content:
				"<div style=\"cursor:pointer;width:50px;height:50px;line-height:52px;font-size:14px;color:white;text-align:center;font-weight:bold;background:url(" +
				HOME_PATH +
				"/img/cluster-marker-5.png);background-size:contain;\"></div>",
			size: new naver.maps.Size(50, 50),
			anchor: new naver.maps.Point(20, 20)
		};

	useEffect(() => {
		if (!naverMap) return;

		// @ts-ignore
		const clustering = new MarkerClustering({
			minClusterSize: 2,
			maxZoom: 16,
			map: naverMap,
			markers: markers,
			disableClickZoom: false,
			gridSize: 20,
			averageCenter: true,
			icons: [htmlMarker1, htmlMarker2, htmlMarker3, htmlMarker4, htmlMarker5],
			indexGenerator: [5, 10, 20, 30, 50],
			// @ts-ignore
			stylingFunction: function (clusterMarker, count) {
				clusterMarker.getElement().querySelector("div:first-child").textContent = count;
			}
		});

		return () => {
			// @ts-ignore
			clustering.setMap(null);
		};
	});

	return <></>;
}
NaverMap.Clustering = Clustering;
NaverMap.Marker = Marker;

export default NaverMap;
